import React from 'react';
import { getBlazemeterMockServicesMaxReplicas } from '../../../../../utils/bzmUserConfigUtil';
import { isLocationScalable, isOplLocation } from '../../../../../utils/mockServiceUtils';
import MockParameters, { MockParameterState } from '../MockParameters';
import { MockService } from '../../../../../types/rest/mockServiceTypes';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store/rootReducer';
import { isTemplate } from '../../../../../utils/miscUtil';
import {
  mockServiceParameterTabChanged,
  updateMockServiceAssets,
} from '../../../../../store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { MsDetailParametersTab } from '../../../../../store/mockServices/mockServiceTypes';
import { AssetAssignmentBundle } from '../SslConfiguration';
import _ from 'lodash';
import { mockServicePatchAction } from '../../../../../store/mockServices/mockServiceList/mockServiceListActions';
import {
  updateTemplateActiveParamTab,
  updateTemplateAssets,
} from '../../../../../store/mockServiceTemplates/mockServiceTemplateDetail/mockServiceTemplateDetailActions';
import { updateMockServiceTemplate } from '../../../../../store/mockServiceTemplates/mockServiceTemplatesActions';

interface Props {
  mockService: MockService;
  disabled: boolean;
  runnerType?: 'HTTP' | 'MESSAGING';
}

/**
 * Wrapper for MockServiceParameter component with handlers.
 * @param mockService
 * @param disabled
 * @constructor
 */
const MockParameterHandlerWrapper = ({ mockService, disabled }: Props) => {
  const locationIsPresent = !!(mockService.harborId && mockService.shipId);
  const isScalable: boolean = locationIsPresent && isLocationScalable(mockService.harborId, mockService.shipId);
  const dispatch = useDispatch();

  const activeParamTab = useSelector((state: ApplicationState) => {
    return !isTemplate(mockService)
      ? state.mockservices.activeParamTab
      : state.mockServiceTemplateDetail.activeParamsTab;
  });

  const assets = useSelector((state: ApplicationState) => {
    return !isTemplate(mockService) ? state.mockServiceDetails.assets : state.mockServiceTemplateDetail.assets;
  });

  const blazeUserConfig = useSelector((state: ApplicationState) => {
    return state.blazeUserConfig.blazeUserConfig;
  });

  const mockServiceParameterTabChangedHandler = (tab: string) => {
    if (!isTemplate(mockService)) {
      dispatch(mockServiceParameterTabChanged(tab as MsDetailParametersTab));
    } else {
      dispatch(updateTemplateActiveParamTab(tab as MsDetailParametersTab));
    }
  };

  const updateMockServiceAssetsHandler = (assetAssignment: AssetAssignmentBundle) => {
    if (!isTemplate(mockService)) {
      dispatch(updateMockServiceAssets(assetAssignment));
    } else {
      dispatch(updateTemplateAssets(assetAssignment));
    }
  };

  const updateParametersHandler = (params: MockParameterState) => {
    const ms = Object.assign({}, mockService, params);
    if (!isTemplate(mockService)) {
      dispatch(mockServicePatchAction(ms));
    } else {
      dispatch(updateMockServiceTemplate(ms));
    }
  };

  const updateParametersDebouncedHandler = _.debounce((params: MockParameterState) => {
    updateParametersHandler(params);
  }, 600);

  return (
    <MockParameters
      state={{ ...mockService }}
      onUpdateState={(state, debounce) => {
        if (debounce) {
          updateParametersDebouncedHandler(state);
        } else {
          updateParametersHandler(state);
        }
      }}
      disabled={disabled}
      isContainerScalable={isScalable}
      mockServicesMaxReplicas={getBlazemeterMockServicesMaxReplicas(blazeUserConfig)}
      assetBundle={assets.assetBundle}
      onCertificatesChanged={updateMockServiceAssetsHandler}
      showSslConfig={isOplLocation(mockService.harborId, mockService.shipId) || isTemplate(mockService)}
      showRateLimits={true}
      showAuthentication={true}
      activeTab={activeParamTab}
      onParamTabChanged={mockServiceParameterTabChangedHandler}
    />
  );
};

export default MockParameterHandlerWrapper;
